import { ApplicationSidebarItem } from "@rms-types/modules/application/types";
import { ConversationSidebarItem } from "@rms-types/modules/conversation/types";
import { DashboardSidebar } from "@rms-types/modules/dashboard/types";
import { EnrollmentSidebarItem } from "@rms-types/modules/enrollment/types";
import { OrganizationSidebarItem } from "@rms-types/modules/organization/types";
import * as Sentry from "@sentry/react";
import { FetchStatus, QueryStatus } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { omit } from "lodash";
import { create } from "zustand";

import {
  applicationEndpoint,
  conversationEndpoint,
  dashboardEndpoint,
  enrollmentEndpoint,
  organizationEndpoint,
} from "@/constants";
import {
  generateApplicationSidebar,
  generateConversationSidebar,
  generateEnrollmentSidebar,
  generateOrganizationSidebar,
} from "@/features";
import { SidebarItem } from "@/layouts/types";

interface DashboardSidebarData<T> {
  data: T;
  listItems: SidebarItem[];
}

export interface DashboardSidebarState {
  applicationSidebar: DashboardSidebarData<ApplicationSidebarItem[]>;
  organizationSidebar: DashboardSidebarData<OrganizationSidebarItem[]>;
  enrollmentSidebar: DashboardSidebarData<EnrollmentSidebarItem[]>;
  conversationSidebar: DashboardSidebarData<ConversationSidebarItem>;
}

export interface DashboardSidebarStore extends DashboardSidebarState {
  status: QueryStatus | FetchStatus;
  error: AxiosError | null;
  isLoading: boolean;

  actions: {
    loadDynamicSidebar: () => Promise<void>;
    refetchApplicationSidebar: () => Promise<void>;
    refetchOrganizationSidebar: () => Promise<void>;
    refetchEnrollmentSidebar: () => Promise<void>;
    refetchConversationSidebar: () => Promise<void>;
    updateConversationSidebar: (data: Partial<ConversationSidebarItem>) => void;
  };
}

type SidebarStoreKey = keyof Pick<
  DashboardSidebarStore,
  "applicationSidebar" | "organizationSidebar" | "enrollmentSidebar" | "conversationSidebar"
>;

const initialSidebarData = {
  data: [],
  listItems: [],
};

const initialConversationSidebarData: DashboardSidebarStore["conversationSidebar"] = {
  data: {
    inbox: [],
    favorites: [],
    trash: [],
    folders: [],
  },
  listItems: [],
};

const initialState: Omit<DashboardSidebarStore, "actions"> = {
  status: "idle",
  isLoading: false,
  error: null,

  applicationSidebar: initialSidebarData,
  organizationSidebar: initialSidebarData,
  enrollmentSidebar: initialSidebarData,
  conversationSidebar: initialConversationSidebarData,
};

const fetchSidebar = async <T>(
  endpoint: string,
  storeKey: SidebarStoreKey,
  updateState: (state: { [key: string]: DashboardSidebarData<T> } | Partial<DashboardSidebarStore>) => void,
  transformData: (data: T) => SidebarItem[]
) => {
  try {
    const { data } = await axios.get<T>(endpoint);
    updateState({
      [storeKey]: {
        data,
        listItems: transformData(data),
      },
    });
  } catch (error) {
    updateState({ isLoading: false, error: error as AxiosError });
  }
};

export const useDashboardSidebarStore = create<DashboardSidebarStore>((set) => {
  const updateState = (
    state: Partial<DashboardSidebarStore> | ((currentState: DashboardSidebarStore) => Partial<DashboardSidebarStore>)
  ) => {
    set((currentState) => ({
      ...currentState,
      ...(typeof state === "function" ? state(currentState) : state),
    }));
  };

  return {
    ...initialState,

    actions: {
      loadDynamicSidebar: async () => {
        try {
          updateState({
            status: "pending",
            isLoading: true,
          });

          const { data } = await axios.get<DashboardSidebar>(dashboardEndpoint.sidebar);
          updateState({
            applicationSidebar: {
              data: data.applications,
              listItems: generateApplicationSidebar(data.applications),
            },
            organizationSidebar: {
              data: data.organizations,
              listItems: generateOrganizationSidebar(data.organizations),
            },
            enrollmentSidebar: {
              data: data.enrollments,
              listItems: generateEnrollmentSidebar(data.enrollments),
            },
            conversationSidebar: {
              data: data.conversation,
              listItems: generateConversationSidebar(data.conversation),
            },
            status: "success",
            isLoading: false,
          });
        } catch (error) {
          updateState({
            error: error as AxiosError,
            isLoading: false,
          });
          Sentry.captureException(error);
        }
      },
      refetchApplicationSidebar: async () => {
        await fetchSidebar<ApplicationSidebarItem[]>(
          applicationEndpoint.applicationSidebar,
          "applicationSidebar",
          updateState,
          generateApplicationSidebar
        );
      },
      refetchOrganizationSidebar: async () => {
        await fetchSidebar<OrganizationSidebarItem[]>(
          organizationEndpoint.organizationSideBar,
          "organizationSidebar",
          updateState,
          generateOrganizationSidebar
        );
      },
      refetchEnrollmentSidebar: async () => {
        await fetchSidebar<EnrollmentSidebarItem[]>(
          enrollmentEndpoint.enrollmentSidebar,
          "enrollmentSidebar",
          updateState,
          generateEnrollmentSidebar
        );
      },
      refetchConversationSidebar: async () => {
        await fetchSidebar<ConversationSidebarItem>(
          conversationEndpoint.conversationSidebar,
          "conversationSidebar",
          updateState,
          generateConversationSidebar
        );
      },
      updateConversationSidebar: (updatedData) =>
        updateState((currentState) => {
          const updatedConversationData = {
            ...currentState.conversationSidebar.data,
            ...updatedData,
          };

          return {
            conversationSidebar: {
              data: updatedConversationData,
              listItems: generateConversationSidebar(updatedConversationData),
            },
          };
        }),
    },
  };
});

export const useDashboardSidebarData = () => useDashboardSidebarStore((state) => omit(state, "actions"));
export const useDashboardSidebarActions = () => useDashboardSidebarStore((state) => state.actions);
